import { yupResolver } from '@hookform/resolvers/yup';
import { Button, FormControl, TextField } from '@material-ui/core';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { makeStyles } from '@material-ui/core/styles';
import React, { useContext, useEffect, useMemo } from 'react';
import { Controller, SubmitHandler, useForm, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CustomSwitch from 'components/Buttons/CustomSwitch';
import DatePicker from 'components/CustomInputs/DatePicker';
import SelectWithErrorField from 'components/CustomInputs/SelectWithErrorField';
import TimePicker from 'components/CustomInputs/TimePicker';
import WeightInputField from 'components/CustomInputs/WeightInputField';
import { AppContext } from 'context';
import {
  useActiveContractToDate,
  useClientsWithActiveContractToDate,
  useStreamsWithClientAndActiveContractToDate,
} from 'hooks';
import { ReceptionForm as ReceptionFormType } from 'types';
import { useLatestClosureDate } from 'utils/closureHooks';
import { isValidDate } from 'utils/date';
import { colors } from 'theme';
import MemoInfoIcon from 'components/Icons/InfoIcon';
import { receptionFormSchema } from './ReceptionForm.yup';
import { MiseEnRefusTooltip, ReceptionRefuseeTooltip } from './ReceptionFormArrowTooltip';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
    paddingBottom: '2.5em',
  },
  fieldLine: {
    marginTop: 8,

    '& > div': {
      minWidth: 470,
    },

    '& span': {
      fontSize: 18,
      marginBottom: 5,
    },
  },
  selectWithError: {
    '& > div': {
      width: '100%',
    },
  },
  switch: {
    marginTop: 20,
    marginBottom: 0,
    width: '20em',
    textAlign: 'right',
  },
  sharedLine: {
    display: 'flex',
    justifyContent: 'space-between',

    '& > div': {
      width: 214,
    },
  },
  tooltipTitle: {
    fontWeight: 'bold',
    fontSize: '0.9em',
  },
  tooltipText: {
    fontWeight: 'normal',
    fontSize: '0.8em',
  },
  tooltipButton: {
    marginLeft: '4.17%',
  },
  infoIcon: {
    color: colors.neutral800,
  },
});

interface NetWeightProps {
  control: any;
  setValue: any;
  trigger: any;
  clearErrors: any;
}

const NetWeightField = ({ control, trigger, setValue, clearErrors }: NetWeightProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'receptionForm' });

  const [grossWeight, tare] = useWatch({
    control,
    name: ['grossWeight', 'tare'],
  });
  const { dirtyFields } = useFormState({
    control,
    name: 'netWeight',
  });
  const isDirty = dirtyFields?.['netWeight'];

  useEffect(() => {
    if (!grossWeight || !tare) {
      if (isDirty) {
        // Net weight has been modified, we should revalidate
        trigger('netWeight');
      }
      return;
    }

    const newNetWeight = grossWeight - tare;
    if (newNetWeight <= 0) {
      setValue('netWeight', '');
      clearErrors('netWeight');
      return;
    }

    setValue('netWeight', newNetWeight, { shouldValidate: true });
  }, [isDirty, trigger, setValue, clearErrors, grossWeight, tare]);

  return (
    <Controller
      name="netWeight"
      control={control}
      render={({ field: { ref, ...fieldProps }, fieldState }) => (
        <WeightInputField
          id="netWeight"
          label={t('netWeight')}
          variant="outlined"
          inputRef={ref}
          {...fieldProps}
          helperText={fieldState.error?.message}
          error={fieldState.invalid}
          InputLabelProps={{ required: true }}
        />
      )}
    />
  );
};

export interface ReceptionFormProps {
  onSubmit: SubmitHandler<ReceptionFormType>;
  reception?: ReceptionFormType;
}

const ReceptionForm: React.FC<ReceptionFormProps> = ({ onSubmit, reception }) => {
  const classes = useStyles();
  const { t } = useTranslation('translation', { keyPrefix: 'receptionForm' });

  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();

  const [closureMonth, twoMonthAfterClosureDate] = useLatestClosureDate(sortingCenter);

  const schema = useMemo(
    () => receptionFormSchema(t, closureMonth, twoMonthAfterClosureDate),
    [t, closureMonth, twoMonthAfterClosureDate],
  );

  const receptionOrEmpty = reception || {};
  const defaultValues = {
    receptionDate: null,
    receptionTime: null,
    client: '',
    stream: '',
    proofOfWeightIdentifier: '',
    grossWeight: '',
    netWeight: null,
    tare: '',
    refused: false,
    incrementRejectStock: false,
    ...receptionOrEmpty,
  };
  const { control, trigger, handleSubmit, watch, setValue, clearErrors } = useForm({
    mode: 'onBlur',
    defaultValues,
    resolver: yupResolver(schema),
  });

  const watchReceptionDate = watch('receptionDate', defaultValues.receptionDate);
  const watchRefused = watch('refused', defaultValues.refused);
  const watchClient = watch('client', defaultValues.client);
  const activeContractsToDate = useActiveContractToDate(watchReceptionDate);
  const clientChoices = useClientsWithActiveContractToDate(activeContractsToDate);
  const streamChoices = useStreamsWithClientAndActiveContractToDate(watchClient, activeContractsToDate);

  useEffect(() => {
    if (false === watchRefused) {
      setValue('incrementRejectStock', false);
    }
  }, [watchRefused, setValue]);

  return (
    <form id="reception_form" onSubmit={handleSubmit(onSubmit)} className={classes.form}>
      <div className={classes.fieldLine}>
        <div className={classes.sharedLine}>
          <Controller
            name="receptionDate"
            control={control}
            render={({ field: { ref, onChange, ...fieldProps }, fieldState }) => (
              <FormControl variant="outlined">
                <DatePicker
                  id="receptionDate"
                  label={t('date')}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  inputRef={ref}
                  onAccept={() => {
                    trigger('receptionDate');
                  }}
                  onChange={e => {
                    onChange(e);
                    setValue('client', '');
                    setValue('stream', '');
                  }}
                  InputLabelProps={{ required: true }}
                  {...fieldProps}
                />
              </FormControl>
            )}
          />
          <Controller
            name="receptionTime"
            control={control}
            render={({ field: { ref, ...fieldProps }, fieldState }) => (
              <FormControl variant="outlined">
                <TimePicker
                  id="receptionTime"
                  label={t('time')}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  InputLabelProps={{ required: true }}
                  inputRef={ref}
                  {...fieldProps}
                />
              </FormControl>
            )}
          />
        </div>
      </div>
      <div className={classes.fieldLine}>
        <Controller
          name="client"
          control={control}
          render={({ field: { ref, onChange, ...inputProps }, fieldState }) => (
            <SelectWithErrorField
              id="client"
              choices={clientChoices}
              choiceDisplayKey="shortName"
              selectLabel={t('client')}
              disabled={null === watchReceptionDate || !isValidDate(watchReceptionDate)}
              error={fieldState.invalid}
              errorMessage={fieldState.error?.message}
              inputRef={ref}
              InputLabelProps={{ required: true }}
              onChange={e => {
                onChange(e);
                setValue('stream', '');
              }}
              {...inputProps}
            />
          )}
        />
      </div>
      <div className={classes.fieldLine}>
        <Controller
          name="stream"
          control={control}
          render={({ field: { ref, ...inputProps }, fieldState }) => (
            <SelectWithErrorField
              id="stream"
              choices={streamChoices}
              choiceDisplayKey="name"
              selectLabel={t('stream')}
              disabled={null === watchReceptionDate || '' === watchClient}
              error={fieldState.invalid}
              errorMessage={fieldState.error?.message}
              inputRef={ref}
              InputLabelProps={{ required: true }}
              {...inputProps}
            />
          )}
        />
      </div>
      <div className={classes.fieldLine}>
        <div className={classes.sharedLine}>
          <FormControl>
            <Controller
              name="grossWeight"
              control={control}
              render={({ field: { ref, ...inputProps }, fieldState }) => (
                <WeightInputField
                  id="grossWeight"
                  label={t('grossWeight')}
                  variant="outlined"
                  inputRef={ref}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  {...inputProps}
                />
              )}
            />
          </FormControl>
          <FormControl>
            <Controller
              name="tare"
              control={control}
              render={({ field: { ref, ...inputProps }, fieldState }) => (
                <WeightInputField
                  id="tare"
                  label={t('tare')}
                  variant="outlined"
                  inputRef={ref}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  {...inputProps}
                />
              )}
            />
          </FormControl>
        </div>
      </div>
      <div className={classes.fieldLine}>
        <div className={classes.sharedLine}>
          <FormControl>
            <NetWeightField control={control} setValue={setValue} trigger={trigger} clearErrors={clearErrors} />
          </FormControl>
          <FormControl>
            <Controller
              name="proofOfWeightIdentifier"
              control={control}
              render={({ field: { ref, ...fieldProps }, fieldState }) => (
                <TextField
                  id="proofOfWeightIdentifier"
                  label={t('proofOfWeightIdentifier')}
                  variant="outlined"
                  inputRef={ref}
                  {...fieldProps}
                  helperText={fieldState.error?.message}
                  error={fieldState.invalid}
                />
              )}
            />
          </FormControl>
        </div>
      </div>
      <div className={`${classes.fieldLine} ${classes.switch}`}>
        <Controller
          name="refused"
          control={control}
          render={({ field: { ref, value, ...fieldProps } }) => (
            <FormControlLabel
              id="refused"
              value="start"
              control={<CustomSwitch checked={value} {...fieldProps} />}
              label={t('refused')}
              labelPlacement="start"
              inputRef={ref}
            />
          )}
        />
        <ReceptionRefuseeTooltip
          title={
            <>
              <p className={classes.tooltipTitle}>{t('tooltip.refused.title')}</p>
              <p className={classes.tooltipText}>{t('tooltip.refused.text')}</p>
            </>
          }
          placement="right"
          arrow
        >
          <Button className={classes.tooltipButton}>
            <MemoInfoIcon width={24} height={24} className={classes.infoIcon} />
          </Button>
        </ReceptionRefuseeTooltip>
      </div>
      <div className={`${classes.fieldLine} ${classes.switch}`}>
        <Controller
          name="incrementRejectStock"
          control={control}
          render={({ field: { ref, value, ...fieldProps } }) => (
            <FormControlLabel
              id="incrementRejectStock"
              disabled={null === watchRefused || false === watchRefused}
              value="start"
              control={<CustomSwitch checked={watchRefused && value} {...fieldProps} />}
              label={t('incrementRejectStock')}
              labelPlacement="start"
              inputRef={ref}
            />
          )}
        />
        <MiseEnRefusTooltip
          title={
            <>
              <p className={classes.tooltipTitle}>{t('tooltip.incrementRejectStock.title')}</p>
              <p className={classes.tooltipText}>{t('tooltip.incrementRejectStock.text')}</p>
            </>
          }
          placement="right"
          arrow
        >
          <Button className={classes.tooltipButton}>
            <MemoInfoIcon width={24} height={24} className={classes.infoIcon} />
          </Button>
        </MiseEnRefusTooltip>
      </div>
    </form>
  );
};

export default ReceptionForm;
